import React, { useRef, useEffect, useCallback, forwardRef, useImperativeHandle, useState } from 'react'

// components
import {
  CCol,
  CFormLabel,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CButton
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilReload } from '@coreui/icons'

// styling
import { styleButtonVars2 } from 'src/components/custom-styles/buttonStyles'

const CAPTCHA_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789' // huruf/angka yang mirip (0,O,1,I) sengaja dihilangkan

const generateCaptchaText = (length) => {
  let result = ''
  for (let i = 0; i < length; i += 1) {
    result += CAPTCHA_CHARS.charAt(Math.floor(Math.random() * CAPTCHA_CHARS.length))
  }
  return result
}

const drawCaptcha = (canvas, text) => {
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  const { width, height } = canvas

  ctx.clearRect(0, 0, width, height)

  ctx.fillStyle = '#f5f5f5'
  ctx.fillRect(0, 0, width, height)

  for (let i = 0; i < 6; i += 1) {
    ctx.strokeStyle = `rgba(${Math.random() * 180}, ${Math.random() * 180}, ${Math.random() * 180}, 0.5)`
    ctx.beginPath()
    ctx.moveTo(Math.random() * width, Math.random() * height)
    ctx.lineTo(Math.random() * width, Math.random() * height)
    ctx.stroke()
  }

  for (let i = 0; i < 35; i += 1) {
    ctx.fillStyle = `rgba(${Math.random() * 180}, ${Math.random() * 180}, ${Math.random() * 180}, 0.6)`
    ctx.beginPath()
    ctx.arc(Math.random() * width, Math.random() * height, 1.4, 0, 2 * Math.PI)
    ctx.fill()
  }

  const charWidth = width / (text.length + 1)
  ctx.font = 'bold 24px Arial'
  ctx.textBaseline = 'middle'
  text.split('').forEach((char, index) => {
    ctx.save()
    const x = charWidth * (index + 0.75)
    const y = height / 2 + (Math.random() * 8 - 4)
    const angle = (Math.random() * 30 - 15) * (Math.PI / 180)
    ctx.translate(x, y)
    ctx.rotate(angle)
    ctx.fillStyle = `rgb(${Math.random() * 90}, ${Math.random() * 90}, ${Math.random() * 90})`
    ctx.fillText(char, 0, 0)
    ctx.restore()
  })
}

// Captcha canvas: generate & gambar kode secara dinamis (panjang & ukuran diatur via props).
// Parent memvalidasi lewat ref: captchaRef.current.validate(inputValue) dan me-refresh
// lewat captchaRef.current.refresh().
const FormCaptcha = forwardRef(({
  id,
  name,
  colSize,
  className,
  inputLabel,
  placeholder = 'Masukkan kode captcha',
  value,
  handleTextInput,
  handleBlur,
  feedbackInvalid,
  invalid,
  combinationIndex,
  disabled,
  length = 5,
  width = 150,
  height = 50
}, ref) => {
  const canvasRef = useRef(null)
  const [captchaText, setCaptchaText] = useState('')

  const refresh = useCallback(() => {
    setCaptchaText(generateCaptchaText(length))
  }, [length])

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    if (captchaText) drawCaptcha(canvasRef.current, captchaText)
  }, [captchaText])

  useImperativeHandle(ref, () => ({
    refresh,
    validate: (inputValue) =>
      typeof inputValue === 'string' &&
      inputValue.trim().length > 0 &&
      inputValue.trim().toUpperCase() === captchaText.toUpperCase(),
  }))

  return (
    <CCol lg={colSize} className={className}>
      {inputLabel && (
        <CFormLabel>
          {inputLabel}
        </CFormLabel>
      )}
      <div className="d-flex align-items-center gap-2 mb-2">
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          style={{ borderRadius: 4, border: '1px solid #d8dbe0', userSelect: 'none' }}
          aria-label="Kode captcha"
        />
        <CButton
          type="button"
          onClick={refresh}
          style={styleButtonVars2}
          size="sm"
          title="Ganti kode captcha"
        >
          <CIcon icon={cilReload} />
        </CButton>
      </div>
      <CInputGroup className="has-validation">
        <CFormInput
          id={id}
          name={name}
          placeholder={placeholder}
          type="text"
          value={value}
          onChange={(e) => handleTextInput(e, combinationIndex)}
          onBlur={handleBlur}
          feedbackInvalid={feedbackInvalid}
          invalid={invalid}
          disabled={disabled}
          autoComplete="off"
        />
      </CInputGroup>
    </CCol>
  )
})

FormCaptcha.displayName = 'FormCaptcha'

export default FormCaptcha